import { Handler, useDrag } from "@use-gesture/react";
import classNames from "classnames";
import {
  action,
  computed,
  IComputedValue,
  IObservableValue,
  observable,
  runInAction,
} from "mobx";
import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useRef, useState } from "react";
import { MEDIUM_WIDTH_BREAKPOINT } from "../Constants";
import { ActivePaneState, PaneType, PaneViewMode, Rect } from "../Types";
import { Tooltip } from "../ui/Tooltip";
import { assert } from "../Utils";
import { canvasViewport, windowViewportAtom } from "./CanvasState";
import { PaneComponentContext } from "./PaneComponentContext";
import { paneMutator } from "./PaneMutator";

function HorizontalExpressionEditorResizer({
  editorWidthBox,
  rootElementRef,
  editorElementRef,
  isResizingEditor,
  setIsResizingEditor,
}: {
  editorWidthBox: IObservableValue<number | undefined>;
  rootElementRef: React.MutableRefObject<HTMLDivElement | null>;
  editorElementRef: React.MutableRefObject<HTMLDivElement | null>;
  isResizingEditor: boolean;
  setIsResizingEditor: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const bindDrag = useDrag(
    action<Handler<"drag">>(
      ({ first, last, initial, xy, memo: startEditorWidth }) => {
        if (first) {
          return editorWidthBox.get() ?? editorElementRef.current!.offsetWidth;
        } else if (last) {
          setIsResizingEditor(false);
        } else {
          const newWidth = Math.max(
            Math.min(
              startEditorWidth + (xy[0] - initial[0]),
              rootElementRef.current!.offsetWidth - 4
            ),
            0
          );
          if (newWidth !== editorWidthBox.get()) {
            editorWidthBox.set(newWidth);
          }
          setIsResizingEditor(true);
          return startEditorWidth;
        }
      }
    )
  );

  return (
    <Tooltip
      content={
        <div className="text-left">
          <div>
            <span className="font-semibold">drag</span> to resize
          </div>
          <div>
            <span className="font-semibold">double-click</span> to reset
          </div>
        </div>
      }
      side="top"
      sideOffset={8}
    >
      <div
        {...bindDrag()}
        onDoubleClick={action(() => {
          if (editorWidthBox.get() !== undefined) {
            editorWidthBox.set(undefined);
          }
        })}
        className="absolute -right-1 top-0 bottom-0 w-[7px] flex flex-col items-center cursor-ew-resize z-1 group touch-none"
      >
        <div
          className={classNames(
            "border-r grow border-neutral-300 opacity-0 group-hover:opacity-100",
            { "opacity-100": isResizingEditor }
          )}
        />
      </div>
    </Tooltip>
  );
}

const ExpressionEditorResizer = observer(
  ({
    paneId,
    paneRectComputed,
    editorHeightComputed,
    paneType,
    editorElementRef,
    isResizingEditor,
    setIsResizingEditor,
  }: {
    paneId: string;
    paneRectComputed: IComputedValue<Rect>;
    editorHeightComputed: IComputedValue<number | undefined>;
    paneType: PaneType;
    editorElementRef: React.MutableRefObject<HTMLDivElement | null>;
    isResizingEditor: boolean;
    setIsResizingEditor: React.Dispatch<React.SetStateAction<boolean>>;
  }) => {
    const { activePaneState } = useContext(PaneComponentContext);

    const bindDrag = useDrag(
      action<Handler<"drag">>(
        ({ first, last, initial, xy, memo: startEditorHeight }) => {
          if (first) {
            return (
              editorHeightComputed.get() ??
              editorElementRef.current!.offsetHeight
            );
          } else if (last) {
            setIsResizingEditor(false);
          } else {
            const newHeight = Math.min(
              Math.max(
                Math.round(
                  (startEditorHeight +
                    (xy[1] - initial[1]) / canvasViewport.zoom) /
                    16
                ) * 16,
                0
              ),
              paneRectComputed.get()[3] -
                (paneType === PaneType.State ? 64 : 32)
            );
            if (newHeight !== editorHeightComputed.get()) {
              paneMutator.updateEditorHeight(paneId, newHeight);
            }
            setIsResizingEditor(true);
            return startEditorHeight;
          }
        }
      )
    );

    return (
      <Tooltip
        content={
          <div className="text-left">
            <div>
              <span className="font-semibold">drag</span> to resize
            </div>
            <div>
              <span className="font-semibold">double-click</span> to reset
            </div>
          </div>
        }
        side="right"
      >
        <div
          {...bindDrag()}
          onDoubleClick={action(() => {
            if (editorHeightComputed.get() !== undefined) {
              paneMutator.updateEditorHeight(paneId, undefined);
            }
          })}
          className={classNames(
            "absolute -bottom-1 left-0 right-0 h-[7px] flex items-center cursor-ns-resize z-1 group touch-none",
            {
              "pointer-events-none":
                activePaneState.viewMode.get() === PaneViewMode.Collapsed,
            }
          )}
        >
          <div
            className={classNames(
              "border-t grow border-neutral-300 opacity-0 group-hover:opacity-100",
              { "opacity-100": isResizingEditor }
            )}
          />
        </div>
      </Tooltip>
    );
  }
);

export const PaneSplit = observer(
  ({
    paneId,
    paneType,
    activePaneState,
    isMaximized,
    inputElement,
    outputElement,
  }: {
    paneId: string;
    paneType: PaneType;
    activePaneState: ActivePaneState;
    isMaximized: boolean;
    inputElement: React.ReactNode;
    outputElement: React.ReactNode;
  }) => {
    const rootElementRef = useRef<HTMLDivElement | null>(null);
    const inputElementRef = useRef<HTMLDivElement | null>(null);
    assert(
      activePaneState.type === PaneType.Evaluate ||
        activePaneState.type === PaneType.EvaluateGlobal ||
        activePaneState.type === PaneType.State ||
        activePaneState.type === PaneType.EvaluateTemplate ||
        activePaneState.type === PaneType.LLM
    );
    const editorHeight = activePaneState.editorHeight.get();
    const [editorWidthBox] = useState(() =>
      observable.box<number | undefined>(undefined)
    );
    const useVerticalSplits = computed(
      () =>
        isMaximized &&
        paneType !== PaneType.State &&
        windowViewportAtom.value[0] >= MEDIUM_WIDTH_BREAKPOINT
    ).get();
    useEffect(() => {
      if (!useVerticalSplits) {
        runInAction(() => {
          editorWidthBox.set(undefined);
        });
      }
    }, [useVerticalSplits]);
    const [isResizingEditor, setIsResizingEditor] = useState(false);

    return (
      <div
        className={classNames("h-full flex relative", {
          "flex-col": !useVerticalSplits,
        })}
        ref={rootElementRef}
      >
        <div
          className={classNames(
            "bg-neutral-50 dark:bg-neutral-100 relative shrink-0 border-neutral-150 dark:border-neutral-200",
            useVerticalSplits
              ? classNames(
                  "border-r",
                  editorWidthBox.get() === undefined ? "w-1/2" : undefined
                )
              : classNames(
                  editorHeight !== 0 ? "border-b" : undefined,
                  editorHeight === undefined
                    ? "max-h-[calc(100%-16px)]"
                    : "max-h-full"
                )
          )}
          style={
            useVerticalSplits
              ? editorWidthBox.get() !== undefined
                ? { width: `${editorWidthBox.get()}px` }
                : undefined
              : editorHeight !== undefined
              ? { height: `${editorHeight}px` }
              : undefined
          }
          ref={inputElementRef}
        >
          {inputElement}
          {useVerticalSplits ? (
            <HorizontalExpressionEditorResizer
              editorWidthBox={editorWidthBox}
              rootElementRef={rootElementRef}
              editorElementRef={inputElementRef}
              isResizingEditor={isResizingEditor}
              setIsResizingEditor={setIsResizingEditor}
            />
          ) : (
            <ExpressionEditorResizer
              paneId={paneId}
              paneRectComputed={activePaneState.rect}
              editorHeightComputed={activePaneState.editorHeight}
              paneType={paneType}
              editorElementRef={inputElementRef}
              isResizingEditor={isResizingEditor}
              setIsResizingEditor={setIsResizingEditor}
            />
          )}
        </div>
        <div className="grow overflow-hidden">{outputElement}</div>
      </div>
    );
  }
);
